import {
    ArrowRightOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    ExportOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import {Button, Card, Empty, Flex, Segmented, Spin, Tag, theme, Tooltip, Typography} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';

import {ApiQuery} from '../../../../gen/api';
import {
    AdminAttentionProduct,
    AdminListingAnalytics,
    AdminListingSeverity,
    AdminMarketplaceType,
} from '../../../../gen/api/models';
import {useNotification} from '../../../../NotificationContext';
import {useScreenInfo} from '../../../../ResponsiveGate';
import {marketplaceLabel} from '../../../helpers/status';
import {
    ATTENTION_FETCH_ALL_LIMIT,
    ATTENTION_FETCH_LIMIT,
    checkExpectations,
    formatAgo,
    formatPercent,
    formatPrice,
    formatShare,
    hoursSince,
    MARKET_DATA_STALE_HOURS,
    parseMoney,
    priceChangeSuggested,
    profit,
    REASON_LABEL,
    RESOLVE_DAYS,
    SEVERITY_DESCRIPTION,
    SEVERITY_LABEL,
    severityTone,
} from '../helpers/slowSelling';
import {FindProductLink} from './FindProductLink';
import {TAG_COLOR} from './PriceAnalyticsParts';
import {SlowSellingProductModal} from './SlowSellingProductModal';
import styles from './SlowSellingProductsWidget.module.less';

const {Text} = Typography;

/** Products shown before "Show more" is clicked. */
const COLLAPSED_SIZE = 8;
/** Products each "Show more" click adds. */
const SHOW_MORE_STEP = 20;
/** Listings shown per product in the "All" view; the rest are summarized as "+N more". */
const LISTINGS_PER_PRODUCT = 3;
const ALL_MARKETPLACES = 'all';

type MarketplaceFilter = AdminMarketplaceType | typeof ALL_MARKETPLACES;
type AttentionSort = 'priority' | 'recent';

const SORT_STORAGE_KEY = 'dashboardAttentionSort';

function loadSort(): AttentionSort {
    try {
        return localStorage.getItem(SORT_STORAGE_KEY) === 'recent' ? 'recent' : 'priority';
    } catch {
        return 'priority';
    }
}
type Token = ReturnType<typeof theme.useToken>['token'];

export function severityColor(severity: AdminListingSeverity, token: Token): string {
    switch (severityTone(severity)) {
        case 'error':
            return token.colorError;
        case 'warning':
            return token.colorWarning;
        case 'muted':
            return token.colorTextQuaternary;
        default:
            return token.colorSuccess;
    }
}

const needsAttention = (listing: AdminListingAnalytics) =>
    listing.severity !== AdminListingSeverity.ListingSeverityOk &&
    listing.severity !== AdminListingSeverity.ListingSeverityNoMarketData;

const ListingLine: React.FC<{listing: AdminListingAnalytics; purchasePrice: number | string}> = ({
    listing,
    purchasePrice,
}) => {
    const {token} = theme.useToken();
    const ourPrice = parseMoney(listing.ourPrice);
    const suggestedPrice = parseMoney(listing.suggestedPrice);
    const changePrice = priceChangeSuggested(listing.ourPrice, listing.suggestedPrice);
    // The suggested price follows the market; it is above ours when we sell cheaper than we could.
    const raise = changePrice && (suggestedPrice ?? 0) > (ourPrice ?? 0);
    const change = changePrice && ourPrice && suggestedPrice !== undefined ? suggestedPrice / ourPrice - 1 : undefined;
    // The market pays less than our minimum payout: the price cannot both sell and meet it.
    const minPayoutTooHigh =
        listing.severity === AdminListingSeverity.ListingSeverityBelowFloor || listing.reasons.includes('below_floor');
    const suggestedProfit = profit(listing.suggestedNet, purchasePrice);
    const listingProfit = profit(listing.ourNet, purchasePrice);
    const reasons = listing.reasons.map((reason) => REASON_LABEL[reason]).join(', ');

    return (
        <div className={styles.listing}>
            <Text
                type="secondary"
                className={styles.marketplace}
            >
                {marketplaceLabel(listing.marketplace)}
            </Text>
            <span className={styles.listingPrice}>
                {changePrice ? (
                    <>
                        <Text
                            delete
                            type="secondary"
                        >
                            {formatPrice(listing.ourPrice)}
                        </Text>{' '}
                        <ArrowRightOutlined style={{fontSize: 10, color: token.colorTextQuaternary}} />{' '}
                        <Tooltip
                            title={`Suggested price: 5% below the market, the price that sells. At it: payout ${formatPrice(listing.suggestedNet)}, profit ${formatPrice(suggestedProfit)}.`}
                        >
                            <span
                                className={styles.priceValue}
                                style={{
                                    color:
                                        suggestedProfit !== undefined && suggestedProfit < 0
                                            ? token.colorError
                                            : raise
                                              ? token.colorSuccess
                                              : token.colorPrimary,
                                }}
                            >
                                {formatPrice(listing.suggestedPrice)}
                            </span>
                        </Tooltip>
                        {change !== undefined ? (
                            <Text
                                type="secondary"
                                className={styles.priceChange}
                            >
                                {formatPercent(change)}
                            </Text>
                        ) : null}
                    </>
                ) : (
                    <span className={styles.priceValue}>{formatPrice(listing.ourPrice)}</span>
                )}
            </span>
            <Tooltip
                title={
                    <>
                        {raise
                            ? 'We sell more than 5% below the market: the price can go up.'
                            : SEVERITY_DESCRIPTION[listing.severity]}
                        {changePrice && listing.gapRatio !== undefined && listing.gapRatio !== null
                            ? ` Our price vs. suggested: ${formatPercent(listing.gapRatio)}.`
                            : null}
                        {reasons ? ` Also: ${reasons}.` : null}
                    </>
                }
            >
                <span>
                    <Tag
                        color={raise ? 'green' : TAG_COLOR[severityTone(listing.severity)]}
                        className={styles.statusTag}
                    >
                        {raise ? 'Below market' : SEVERITY_LABEL[listing.severity]}
                    </Tag>
                    {minPayoutTooHigh && listing.severity !== AdminListingSeverity.ListingSeverityBelowFloor ? (
                        <span style={{color: token.colorWarningText}}>min payout too high</span>
                    ) : null}
                </span>
            </Tooltip>
            <span
                className={styles.listingMeta}
                style={{color: token.colorTextTertiary}}
            >
                <Tooltip title="Market price with shipping; payout is what comes to us at our price after fees and shipping; profit is payout minus the purchase price">
                    <span>
                        market {formatPrice(listing.referencePrice)} · payout {formatPrice(listing.ourNet)} · profit{' '}
                        <span
                            style={
                                listingProfit !== undefined && listingProfit < 0
                                    ? {color: token.colorErrorText}
                                    : undefined
                            }
                        >
                            {formatPrice(listingProfit)}
                        </span>
                    </span>
                </Tooltip>
                {' · '}
                <ClockCircleOutlined />{' '}
                <span
                    style={{
                        color:
                            listing.severity === AdminListingSeverity.ListingSeverityStale ||
                            listing.reasons.includes('stale')
                                ? token.colorWarningText
                                : undefined,
                    }}
                >
                    {listing.daysListed}d listed
                </span>
                {' · '}
                price changed {listing.daysSincePriceChange}d ago
            </span>
            {listing.link ? (
                <Tooltip title="Open listing">
                    <a
                        href={listing.link}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.listingLink}
                        onClick={(event) => event.stopPropagation()}
                        style={{color: token.colorTextTertiary}}
                    >
                        <ExportOutlined />
                    </a>
                </Tooltip>
            ) : null}
        </div>
    );
};

export const SlowSellingProductsWidget: React.FC = () => {
    const {token} = theme.useToken();
    const {screenMap} = useScreenInfo();
    const notify = useNotification();
    // Products resolved during this visit stay in place (dimmed) so the action can be undone.
    const [justResolved, setJustResolved] = useState<Set<string>>(() => new Set());
    const [selectedItem, setSelectedItem] = useState<AdminAttentionProduct | null>(null);
    const [marketplaceFilter, setMarketplaceFilter] = useState<MarketplaceFilter>(ALL_MARKETPLACES);
    // How many products are shown; Infinity after "Show all".
    const [visibleCount, setVisibleCount] = useState(COLLAPSED_SIZE);
    const [sort, setSort] = useState<AttentionSort>(loadSort);
    // A new sort or filter starts from the top again.
    useEffect(() => setVisibleCount(COLLAPSED_SIZE), [sort, marketplaceFilter]);

    useEffect(() => {
        try {
            localStorage.setItem(SORT_STORAGE_KEY, sort);
        } catch {
            // Storage may be unavailable (private mode); the choice then lasts for this visit.
        }
    }, [sort]);

    // The first fetch covers "Show more" for a while; when it was cut at the
    // limit, showing more than it holds loads everything.
    const [fetchAll, setFetchAll] = useState(false);
    // The worker keeps changing this list, so it is never served from the cache
    // alone (the app default is staleTime: Infinity): opening the dashboard or
    // switching back to a sort refetches it.
    const attentionQuery = ApiQuery.useAdminDashboardAttentionQuery(
        {limit: fetchAll ? ATTENTION_FETCH_ALL_LIMIT : ATTENTION_FETCH_LIMIT, sort},
        {refetchOnWindowFocus: false, keepPreviousData: true, staleTime: 0}
    );
    const resolveMutation = ApiQuery.useAdminResolveAttentionMutation();
    const unresolveMutation = ApiQuery.useAdminUnresolveAttentionMutation();

    const data = attentionQuery.data;
    const flagged = useMemo(() => data?.items ?? [], [data]);
    const marketplaceCounts = data?.marketplaceCounts ?? [];

    const activeFilter = marketplaceCounts.some(({marketplace}) => marketplace === marketplaceFilter)
        ? marketplaceFilter
        : ALL_MARKETPLACES;

    const items = useMemo(() => {
        const filtered =
            activeFilter === ALL_MARKETPLACES
                ? flagged
                : flagged.filter((item) =>
                      item.listings.some((listing) => listing.marketplace === activeFilter && needsAttention(listing))
                  );
        return filtered;
    }, [flagged, activeFilter]);

    const visibleItems = items.slice(0, visibleCount);
    // The API cut the first fetch at the limit: there may be more products than loaded.
    const mayHaveMore = !fetchAll && flagged.length >= ATTENTION_FETCH_LIMIT;
    const hiddenCount = items.length - visibleItems.length;

    const showMore = () => {
        const next = visibleCount + SHOW_MORE_STEP;
        setVisibleCount(next);
        if (mayHaveMore && next > items.length) {
            setFetchAll(true);
        }
    };
    const showAll = () => {
        setVisibleCount(Infinity);
        if (mayHaveMore) {
            setFetchAll(true);
        }
    };

    const markResolved = (productId: string, resolved: boolean) =>
        setJustResolved((prev) => {
            const next = new Set(prev);
            if (resolved) {
                next.add(productId);
            } else {
                next.delete(productId);
            }
            return next;
        });

    const handleResolve = (item: AdminAttentionProduct) => {
        markResolved(item.productId, true);
        setSelectedItem(null);
        resolveMutation.mutate(
            {id: item.productId, params: {days: RESOLVE_DAYS}},
            {
                onError: () => {
                    markResolved(item.productId, false);
                    notify.openNotificationWithIcon('error', 'Failed to resolve the product');
                },
            }
        );
    };

    const handleUndo = (item: AdminAttentionProduct) => {
        markResolved(item.productId, false);
        unresolveMutation.mutate(
            {productId: item.productId},
            {
                onError: () => {
                    markResolved(item.productId, true);
                    notify.openNotificationWithIcon('error', 'Failed to undo resolve');
                },
            }
        );
    };

    const renderProduct = (item: AdminAttentionProduct) => {
        const isResolved = justResolved.has(item.productId);
        const listings =
            activeFilter === ALL_MARKETPLACES
                ? item.listings.slice(0, LISTINGS_PER_PRODUCT)
                : item.listings.filter((listing) => listing.marketplace === activeFilter);
        const hiddenListings = activeFilter === ALL_MARKETPLACES ? item.listings.length - listings.length : 0;
        const marketplaceCount = item.listings.length;
        const expectations = checkExpectations(item.listings, item.msrp, item.lowestResalePrice);

        return (
            <div
                key={item.productId}
                className={`${styles.row} ${isResolved ? styles.resolvedRow : ''}`}
                onClick={isResolved ? undefined : () => setSelectedItem(item)}
            >
                <div className={styles.header}>
                    <div
                        className={styles.severityBar}
                        style={{background: severityColor(item.worstSeverity, token)}}
                    />
                    {item.thumbnailUrl ? (
                        <img
                            src={item.thumbnailUrl}
                            alt=""
                            className={styles.thumb}
                        />
                    ) : (
                        <div
                            className={styles.thumb}
                            style={{background: token.colorFillTertiary}}
                        />
                    )}
                    <div style={{minWidth: 0}}>
                        <Text
                            strong
                            ellipsis
                            style={{display: 'block'}}
                        >
                            {item.name}
                        </Text>
                        <Text
                            type="secondary"
                            style={{fontSize: 12}}
                        >
                            <Tooltip title="Open the product">
                                <Link
                                    to={`/products/${item.productId}`}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    #{item.listingNumber}
                                </Link>
                            </Tooltip>{' '}
                            ·{' '}
                            {item.marketCapturedAt ? (
                                <>
                                    <Tooltip title="When the market data of this product was last taken from eBay">
                                        market {formatAgo(item.marketCapturedAt)}
                                    </Tooltip>{' '}
                                    ·{' '}
                                </>
                            ) : null}
                            <Tooltip
                                title={
                                    expectations.msrpMisleading
                                        ? `The used market is ${formatPrice(expectations.marketPrice)}, only ${formatShare(expectations.marketToMsrp)} of MSRP: MSRP is a misleading anchor for this item.`
                                        : 'Manufacturer retail price of a new item'
                                }
                            >
                                <span style={expectations.msrpMisleading ? {color: token.colorWarningText} : undefined}>
                                    MSRP {formatPrice(item.msrp)}
                                    {expectations.msrpMisleading
                                        ? ` (market ${formatShare(expectations.marketToMsrp)})`
                                        : null}
                                </span>
                            </Tooltip>
                            <FindProductLink
                                asin={item.asin}
                                name={item.name}
                            />
                            {parseMoney(item.lowestResalePrice) ? (
                                <Tooltip
                                    title={
                                        expectations.minPayoutTooHigh && expectations.bestPayout
                                            ? `Expectation too high: at market prices the best payout is ${formatPrice(expectations.bestPayout.value)} (${marketplaceLabel(expectations.bestPayout.marketplace)}).`
                                            : 'Lowest resale price: the least we want to get for the item after fees and shipping'
                                    }
                                >
                                    {' · '}
                                    <span
                                        style={
                                            expectations.minPayoutTooHigh ? {color: token.colorErrorText} : undefined
                                        }
                                    >
                                        min payout {formatPrice(item.lowestResalePrice)}
                                        {expectations.minPayoutTooHigh && expectations.bestPayout
                                            ? ` (market gives ${formatPrice(expectations.bestPayout.value)})`
                                            : null}
                                    </span>
                                </Tooltip>
                            ) : null}
                        </Text>
                    </div>
                    {isResolved ? (
                        <Text
                            type="secondary"
                            style={{fontSize: 12}}
                        >
                            Hidden for {RESOLVE_DAYS} days
                        </Text>
                    ) : (
                        <Tag
                            className={styles.marketplaceCount}
                            style={{margin: 0}}
                        >
                            {marketplaceCount} marketplace{marketplaceCount === 1 ? '' : 's'}
                        </Tag>
                    )}
                    {isResolved ? (
                        <Button
                            size="small"
                            type="link"
                            style={{padding: 0}}
                            onClick={() => handleUndo(item)}
                        >
                            Undo
                        </Button>
                    ) : (
                        <Tooltip title={`Resolve: hide from this list for ${RESOLVE_DAYS} days, for all admins`}>
                            <Button
                                size="small"
                                type="text"
                                aria-label="Resolve"
                                className={styles.resolveButton}
                                icon={<CheckOutlined />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleResolve(item);
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
                {isResolved
                    ? null
                    : listings.map((listing) => (
                          <ListingLine
                              key={listing.listingId}
                              listing={listing}
                              purchasePrice={item.purchasePrice}
                          />
                      ))}
                {!isResolved && hiddenListings > 0 ? (
                    <Text
                        type="secondary"
                        className={styles.moreListings}
                    >
                        +{hiddenListings} more marketplace{hiddenListings === 1 ? '' : 's'}
                    </Text>
                ) : null}
            </div>
        );
    };

    const sortControl = (
        <Tooltip title="Priority: worst problems first. Recent: products with the freshest market data first.">
            <Segmented<AttentionSort>
                size="small"
                value={sort}
                onChange={setSort}
                options={[
                    {label: 'Priority', value: 'priority'},
                    {label: 'Recent', value: 'recent'},
                ]}
            />
        </Tooltip>
    );

    const marketplaceFilterControl =
        marketplaceCounts.length > 1 ? (
            <Segmented<MarketplaceFilter>
                size="small"
                value={activeFilter}
                onChange={setMarketplaceFilter}
                options={[
                    {label: 'All', value: ALL_MARKETPLACES},
                    ...marketplaceCounts.map(({marketplace, count}) => ({
                        label: `${marketplaceLabel(marketplace)} ${count}`,
                        value: marketplace,
                    })),
                ]}
            />
        ) : null;

    const marketAge = hoursSince(data?.marketDataUpdatedAt);
    const loading = attentionQuery.isLoading;

    return (
        <Card
            size="small"
            title={
                <Flex
                    align="center"
                    gap={8}
                >
                    <WarningOutlined style={{color: token.colorWarning}} />
                    <span>Needs attention</span>
                    <Tag style={{margin: 0}}>{items.length}</Tag>
                </Flex>
            }
            extra={
                screenMap.md ? (
                    <Flex
                        gap={8}
                        align="center"
                    >
                        {sortControl}
                        {marketplaceFilterControl}
                    </Flex>
                ) : null
            }
            styles={{body: {paddingBottom: 0}}}
        >
            {screenMap.md ? null : (
                <Flex
                    vertical
                    gap={8}
                    style={{overflowX: 'auto', marginBottom: 8}}
                >
                    {sortControl}
                    {marketplaceFilterControl}
                </Flex>
            )}
            <Spin spinning={loading}>
                {attentionQuery.isError ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Failed to load the analytics"
                    />
                ) : !loading && items.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            data?.noMarketDataProducts
                                ? 'Nothing to fix among products with market data'
                                : 'All listings are priced in line and selling'
                        }
                    />
                ) : (
                    <div
                        className={styles.list}
                        style={
                            {
                                '--row-border': token.colorBorderSecondary,
                                '--row-hover': token.colorFillQuaternary,
                            } as React.CSSProperties
                        }
                    >
                        {visibleItems.map(renderProduct)}
                    </div>
                )}
            </Spin>

            <Flex
                justify="space-between"
                align="center"
                gap={8}
                wrap
                style={{
                    margin: '0 -12px',
                    padding: '8px 12px',
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                }}
            >
                <Tooltip title="Compared with eBay comparables (price + shipping); other marketplaces are derived from eBay. Worst listing first. Click a product for details.">
                    <Text
                        type="secondary"
                        style={{fontSize: 12}}
                    >
                        Market data{' '}
                        <span
                            style={{
                                color:
                                    marketAge === undefined || marketAge > MARKET_DATA_STALE_HOURS
                                        ? token.colorWarningText
                                        : undefined,
                            }}
                        >
                            {marketAge === undefined
                                ? 'not collected yet'
                                : `updated ${formatAgo(data?.marketDataUpdatedAt)}`}
                        </span>
                        {data?.noMarketDataProducts
                            ? ` · ${data.noMarketDataProducts} product${data.noMarketDataProducts === 1 ? '' : 's'} without market data`
                            : null}
                    </Text>
                </Tooltip>
                <Flex gap={12}>
                    {hiddenCount > 0 || mayHaveMore ? (
                        <>
                            <Button
                                size="small"
                                type="link"
                                style={{padding: 0}}
                                onClick={showMore}
                            >
                                Show more
                            </Button>
                            <Button
                                size="small"
                                type="link"
                                style={{padding: 0}}
                                onClick={showAll}
                            >
                                {mayHaveMore ? 'Show all' : `Show all ${items.length}`}
                            </Button>
                        </>
                    ) : null}
                    {fetchAll && attentionQuery.isFetching ? <Spin size="small" /> : null}
                    {visibleCount > COLLAPSED_SIZE && items.length > COLLAPSED_SIZE ? (
                        <Button
                            size="small"
                            type="link"
                            style={{padding: 0}}
                            onClick={() => setVisibleCount(COLLAPSED_SIZE)}
                        >
                            Show less
                        </Button>
                    ) : null}
                </Flex>
            </Flex>

            {selectedItem ? (
                <SlowSellingProductModal
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    onResolve={handleResolve}
                />
            ) : null}
        </Card>
    );
};
